import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Select, SimpleGrid, Spinner, Badge,
  Stat, StatLabel, StatNumber, Icon, Table, Thead, Tbody, Tr, Th, Td, Tooltip,
  Tabs, TabList, TabPanels, Tab, TabPanel,
} from '@chakra-ui/react';
import { FiRefreshCw, FiUsers, FiCalendar, FiMail, FiBookOpen, FiActivity } from 'react-icons/fi';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';

const API_BASE = '/api/v1';

const METRIC_CONFIG = {
  profiles:        { label: 'Total Profiles',  color: '#805AD5', icon: FiUsers },
  active_profiles: { label: 'Active Profiles', color: '#38A169', icon: FiUsers },
  organisers:      { label: 'Organisers',       color: '#D69E2E', icon: FiUsers },
  attendees:       { label: 'Attendees',        color: '#3182CE', icon: FiUsers },
  events:          { label: 'Events',           color: '#E53E3E', icon: FiCalendar },
  event_instances: { label: 'Event Instances',  color: '#DD6B20', icon: FiCalendar },
  registrations:   { label: 'Registrations',    color: '#319795', icon: FiActivity },
  emails_sent:     { label: 'Emails Sent',      color: '#B83280', icon: FiMail },
  courses:         { label: 'Courses',          color: '#6B46C1', icon: FiBookOpen },
  course_sessions: { label: 'Course Sessions',  color: '#2B6CB0', icon: FiBookOpen },
};

const CHART_COLORS = ['#805AD5', '#38A169', '#3182CE', '#E53E3E', '#D69E2E', '#DD6B20', '#319795', '#B83280', '#6B46C1', '#2B6CB0'];

function InstanceStatistics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [selectedInstance, setSelectedInstance] = useState('');

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchStats = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ days });
    if (selectedInstance) params.append('instance_id', selectedInstance);
    fetch(`${API_BASE}/statistics/?${params}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days, selectedInstance]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (data?.granularity === 'hour') {
      return `${d.getDate()}/${d.getMonth() + 1} ${d.getHours()}:00`;
    }
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="purple.500" /></Box>;
  if (!data) return <Text color="gray.500" textAlign="center" py={20}>Failed to load statistics</Text>;

  const { metric_names = [], series = {}, snapshots = [] } = data;

  // Build combined time-series data for the overview chart
  const allTimes = new Set();
  Object.values(series).forEach(s => s.forEach(p => allTimes.add(p.time)));
  const sortedTimes = Array.from(allTimes).sort();
  const combinedData = sortedTimes.map(time => {
    const point = { time };
    metric_names.forEach(m => {
      const match = (series[m] || []).find(p => p.time === time);
      if (match) point[m] = match.value;
    });
    return point;
  });

  // Key metrics for stat cards (from first snapshot or sum)
  const totals = {};
  snapshots.forEach(snap => {
    Object.entries(snap.metrics).forEach(([k, v]) => {
      totals[k] = (totals[k] || 0) + v;
    });
  });

  // Group metrics for charting
  const peopleMetrics = ['profiles', 'active_profiles', 'organisers', 'attendees'].filter(m => metric_names.includes(m));
  const activityMetrics = ['events', 'event_instances', 'registrations', 'emails_sent'].filter(m => metric_names.includes(m));
  const courseMetrics = ['courses', 'course_sessions'].filter(m => metric_names.includes(m));

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Instance Statistics</Heading>
          <Text color="gray.500">
            Live metrics reported by API instances
            {data.granularity && <Badge ml={2} colorScheme="purple">{data.granularity === 'hour' ? 'Hourly' : 'Daily'}</Badge>}
          </Text>
        </VStack>
        <HStack>
          <Select value={selectedInstance} onChange={(e) => setSelectedInstance(e.target.value)} w="200px" bg="white" placeholder="All instances">
            {snapshots.map(s => (
              <option key={s.instance_id} value={s.instance_id}>{s.instance_name}</option>
            ))}
          </Select>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} w="140px" bg="white">
            <option value={1}>Last 24h</option>
            <option value={3}>Last 3 days</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </Select>
          <Button leftIcon={<FiRefreshCw />} colorScheme="purple" onClick={fetchStats}>Refresh</Button>
        </HStack>
      </HStack>

      {/* Summary Stat Cards */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4} mb={8}>
        {['profiles', 'active_profiles', 'events', 'registrations', 'emails_sent'].map(key => {
          const cfg = METRIC_CONFIG[key];
          if (!cfg || totals[key] === undefined) return null;
          return (
            <Stat key={key} bg="white" p={5} borderRadius="lg" boxShadow="md">
              <HStack>
                <Box p={2} bg="purple.50" borderRadius="lg">
                  <Icon as={cfg.icon} boxSize={5} color="purple.500" />
                </Box>
                <VStack align="start" spacing={0}>
                  <StatLabel color="gray.500" fontSize="xs">{cfg.label}</StatLabel>
                  <StatNumber fontSize="xl">{Math.round(totals[key]).toLocaleString()}</StatNumber>
                </VStack>
              </HStack>
            </Stat>
          );
        })}
      </SimpleGrid>

      {metric_names.length === 0 ? (
        <Box bg="white" p={10} borderRadius="lg" boxShadow="md" textAlign="center">
          <Text color="gray.400" fontSize="lg">No metrics data yet</Text>
          <Text color="gray.400" fontSize="sm" mt={2}>Statistics will appear once API instances begin reporting metrics.</Text>
        </Box>
      ) : (
        <Tabs colorScheme="purple">
          <TabList>
            <Tab>People</Tab>
            <Tab>Activity</Tab>
            {courseMetrics.length > 0 && <Tab>Courses</Tab>}
            <Tab>By Instance</Tab>
          </TabList>
          <TabPanels>
            {/* People Tab */}
            <TabPanel px={0}>
              <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
                <Heading size="sm" mb={4}>Profiles Over Time</Heading>
                {combinedData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={combinedData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tickFormatter={formatTime} fontSize={12} />
                      <YAxis fontSize={12} />
                      <RechartsTooltip labelFormatter={(v) => new Date(v).toLocaleString()} />
                      <Legend />
                      {peopleMetrics.map((m, i) => (
                        <Area key={m} type="monotone" dataKey={m} name={METRIC_CONFIG[m]?.label || m}
                          stroke={METRIC_CONFIG[m]?.color || CHART_COLORS[i]} fill={METRIC_CONFIG[m]?.color || CHART_COLORS[i]}
                          fillOpacity={0.1} strokeWidth={2} />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <Text color="gray.400" textAlign="center" py={10}>No data</Text>}
              </Box>
            </TabPanel>

            {/* Activity Tab */}
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                {activityMetrics.map(m => (
                  <Box key={m} bg="white" p={6} borderRadius="lg" boxShadow="md">
                    <Heading size="sm" mb={4}>{METRIC_CONFIG[m]?.label || m}</Heading>
                    {(series[m] || []).length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={series[m]}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tickFormatter={formatTime} fontSize={12} />
                          <YAxis fontSize={12} />
                          <RechartsTooltip labelFormatter={(v) => new Date(v).toLocaleString()} />
                          <Bar dataKey="value" name={METRIC_CONFIG[m]?.label} fill={METRIC_CONFIG[m]?.color || '#805AD5'} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <Text color="gray.400" textAlign="center" py={8}>No data</Text>}
                  </Box>
                ))}
              </SimpleGrid>
            </TabPanel>

            {/* Courses Tab */}
            {courseMetrics.length > 0 && (
              <TabPanel px={0}>
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  {courseMetrics.map(m => (
                    <Box key={m} bg="white" p={6} borderRadius="lg" boxShadow="md">
                      <Heading size="sm" mb={4}>{METRIC_CONFIG[m]?.label || m}</Heading>
                      {(series[m] || []).length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <AreaChart data={series[m]}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" tickFormatter={formatTime} fontSize={12} />
                            <YAxis fontSize={12} />
                            <RechartsTooltip labelFormatter={(v) => new Date(v).toLocaleString()} />
                            <Area type="monotone" dataKey="value" name={METRIC_CONFIG[m]?.label}
                              stroke={METRIC_CONFIG[m]?.color} fill={METRIC_CONFIG[m]?.color} fillOpacity={0.15} />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : <Text color="gray.400" textAlign="center" py={8}>No data</Text>}
                    </Box>
                  ))}
                </SimpleGrid>
              </TabPanel>
            )}

            {/* By Instance Tab */}
            <TabPanel px={0}>
              <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Instance</Th>
                      <Th>Type</Th>
                      {metric_names.map(m => (
                        <Th key={m} isNumeric>
                          <Tooltip label={METRIC_CONFIG[m]?.label || m}>
                            <Text cursor="help">{(METRIC_CONFIG[m]?.label || m).replace(/^Total /, '').slice(0, 12)}</Text>
                          </Tooltip>
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {snapshots.map(snap => (
                      <Tr key={snap.instance_id} _hover={{ bg: 'gray.50' }}>
                        <Td fontWeight="medium">{snap.instance_name}</Td>
                        <Td><Badge colorScheme={snap.is_lite ? 'orange' : 'purple'}>{snap.is_lite ? 'Lite' : 'Full'}</Badge></Td>
                        {metric_names.map(m => (
                          <Td key={m} isNumeric>{snap.metrics[m] != null ? Math.round(snap.metrics[m]).toLocaleString() : '—'}</Td>
                        ))}
                      </Tr>
                    ))}
                    {snapshots.length === 0 && (
                      <Tr><Td colSpan={metric_names.length + 2} textAlign="center" color="gray.400">No instance data</Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
}

export default InstanceStatistics;
